import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, Reveal, Overline, CTAButton } from "../components/Kinetic";
import { Check } from "lucide-react";

const TransparencePage = () => {
  const { t } = useLanguage();
  useSEO(t("transparence.metaTitle"), t("transparence.metaDesc"));
  const rules = t("transparence.rules");
  const govItems = t("transparence.govItems");
  const pending = t("transparence.pending");

  return (
    <div data-testid="transparence-page">
      <PageHero
        overline={t("transparence.overline")}
        titleLines={t("transparence.titleLines")}
        intro={t("transparence.intro")}
        testid="transparence"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal y={16}>
            <Overline>{t("transparence.ruleTitle")}</Overline>
          </Reveal>
          <div className="mt-12 grid gap-px bg-graphite/10 md:grid-cols-3">
            {rules.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.1} className="bg-bone p-8 md:p-10">
                <p className="font-mono text-sm text-forest">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-graphite">{r.label}</h2>
                <p className="mt-4 text-sm leading-relaxed text-graphite-soft">{r.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-graphite/10 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal y={16}>
            <Overline>{t("threeStates.overline")}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-3xl font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05] text-graphite">
              {t("threeStates.title")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-graphite/10 md:grid-cols-3" data-testid="transparence-three-states">
            {t("threeStates.cols").map((col, i) => (
              <Reveal key={col.label} delay={i * 0.1} className="bg-bone p-8">
                <p className="font-mono text-sm text-forest">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight text-graphite">{col.label}</h3>
                <ul className="mt-5 space-y-3">
                  {col.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm leading-relaxed text-graphite-soft">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-forest" />
                      {it}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2">
          <div>
            <Reveal y={16}>
              <Overline>{t("transparence.govOverline")}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-light tracking-tight text-graphite">
                {t("transparence.govTitle")}
              </h2>
            </Reveal>
            <ul className="mt-10 space-y-0">
              {govItems.map((item) => (
                <Reveal key={item} y={12}>
                  <li className="flex items-start gap-4 border-b border-graphite/10 py-4">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-forest" />
                    <span className="text-sm md:text-base text-graphite">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.1}>
            <div className="border border-science/20 bg-science-soft/50 p-8 md:p-10" data-testid="transparence-pending-box">
              <Overline className="text-science">{t("transparence.pendingOverline")}</Overline>
              <h3 className="mt-4 font-display text-2xl font-light tracking-tight text-graphite">
                {t("transparence.pendingTitle")}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-graphite-soft">{t("transparence.pendingText")}</p>
              <ul className="mt-6 space-y-3">
                {pending.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-graphite">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-science" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-graphite py-20 text-white md:py-28">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6">
          <Reveal>
            <p className="max-w-2xl font-display text-2xl md:text-3xl font-light tracking-tight leading-snug">
              {t("histoire.legendText")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <CTAButton to="/ressources" variant="light" testid="transparence-cta-ressources">
              {t("nav.ressources")}
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default TransparencePage;
